import type { ReviewDetail as ReviewDetailType, FindingSeverity } from './types';

interface ReviewDetailProps {
  detail: ReviewDetailType;
}

const SEVERITY_STYLES: Record<FindingSeverity, string> = {
  blocking: 'bg-red-900 text-red-300',
  issue: 'bg-yellow-900 text-yellow-300',
  nit: 'bg-gray-700 text-gray-300',
};

type TlpRisk = NonNullable<ReviewDetailType['tlpRisk']>;

const TLP_STYLES: Record<TlpRisk, string> = {
  red: 'bg-red-900 text-red-300',
  yellow: 'bg-yellow-900 text-yellow-300',
  green: 'bg-green-900 text-green-300',
};

type ReviewerStatus = 'complete' | 'pending' | 'failed';

const REVIEWER_STATUS_ICONS: Record<ReviewerStatus, string> = {
  complete: '\u2705',
  pending: '\u23f3',
  failed: '\u274c',
};

export function ReviewDetail({ detail }: ReviewDetailProps) {
  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">
            <a
              href={detail.prUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-400 hover:underline"
              aria-label="GitHub PR"
            >
              #{detail.prNumber}
            </a>
            <span className="ml-2 text-gray-300">{detail.prTitle}</span>
          </h3>
          <p className="text-sm text-gray-500 mt-1">{detail.repo}</p>
        </div>
        <div className="flex items-center gap-3">
          {detail.tlpRisk && (
            <span
              data-testid="tlp-badge"
              className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${TLP_STYLES[detail.tlpRisk]}`}
            >
              {detail.tlpRisk.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      {/* Epoch info */}
      <div className="flex gap-4 text-xs text-gray-500">
        <span>Epoch {detail.epoch}</span>
        {detail.staleDiscardedCount != null && (
          <span>{detail.staleDiscardedCount} stale discarded</span>
        )}
      </div>

      {/* Findings table */}
      <div>
        <h4 className="text-sm font-medium text-gray-300 mb-2">Findings ({detail.findings.length})</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 border-b border-gray-700">
              <tr>
                <th className="px-3 py-2 text-left">Severity</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Message</th>
                <th className="px-3 py-2 text-left">Location</th>
                <th className="px-3 py-2 text-left">Reviewer</th>
                <th className="px-3 py-2 text-left">Round</th>
              </tr>
            </thead>
            <tbody>
              {detail.findings.map((finding) => (
                <tr key={finding.id} className="border-b border-gray-800">
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${SEVERITY_STYLES[finding.severity]}`}>
                      {finding.severity}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-400 capitalize">{finding.status}</td>
                  <td className="px-3 py-2 text-gray-300">{finding.message}</td>
                  <td className="px-3 py-2">
                    {finding.githubCommentUrl ? (
                      <a
                        href={finding.githubCommentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-400 hover:underline font-mono text-xs"
                      >
                        {finding.file}:{finding.line}
                      </a>
                    ) : (
                      <span className="font-mono text-xs text-gray-400">
                        {finding.file}:{finding.line}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{finding.reviewer}</td>
                  <td className="px-3 py-2 text-gray-400">{finding.round}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Round history */}
      <div>
        <h4 className="text-sm font-medium text-gray-300 mb-2">Round History</h4>
        <div className="space-y-3">
          {detail.rounds.map((round) => (
            <div key={round.round} className="bg-gray-800 rounded p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-200">Round {round.round}</span>
                {round.timestamp && (
                  <span className="text-xs text-gray-500">{new Date(round.timestamp).toLocaleTimeString()}</span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-gray-400">{round.findingsCount} findings</span>
                <span className="text-gray-400">{round.reviewerCount} reviewers</span>
                {round.converged && (
                  <span className="text-green-400">Converged</span>
                )}
                {round.durationMs > 0 && (
                  <span className="text-gray-500">{Math.round(round.durationMs / 1000)}s</span>
                )}
                {round.reviewers && (
                  <div className="flex items-center gap-2">
                    {round.reviewers.map((reviewer) => (
                      <span key={reviewer.name} className="inline-flex items-center gap-1 text-gray-400">
                        <span>{reviewer.name}</span>
                        <span>{REVIEWER_STATUS_ICONS[reviewer.status] ?? '?'}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
